import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, MessageSquare, FileText, AlertCircle } from 'lucide-react';
import TemplateVariableHandler from '../common/TemplateVariableHandler';

const NodeSidebar = ({ node, templates, onUpdate, onDelete, onClose, loading, botStatus }) => {
  const [formData, setFormData] = useState({
    label: '',
    type: 'template', // Changed from 'freeText' to 'template'
    content: '',
    templateId: null
    // Variable fields removed - now stored in templateVariables state
  });

  const [errors, setErrors] = useState({});
  const [templateVariables, setTemplateVariables] = useState({});

  useEffect(() => {
    if (node) {
      setFormData({
        label: node.data.label || '',
        type: node.data.type || 'template', // Changed from 'freeText' to 'template'
        content: node.data.content || '',
        templateId: node.data.templateId || null
      });
      
      // Initialize templateVariables from node data
      // First, get the template to extract all variables
      const selectedTemplate = templates.find(t => t.templateId === node.data.templateId);
      
      if (selectedTemplate) {
        // Extract all variables from template
        const extractVariables = (content) => {
          if (!content) return [];
          const matches = content.match(/\{\{(\d+)\}\}/g);
          return matches ? [...new Set(matches)] : [];
        };
        
        let bodyVariables = selectedTemplate.bodyVariables || [];
        let headerVariables = selectedTemplate.headerVariables || [];
        
        // Fallback: extract from content if backend variables not available
        if (bodyVariables.length === 0 && selectedTemplate.content) {
          bodyVariables = extractVariables(selectedTemplate.content);
        }
        if (headerVariables.length === 0 && selectedTemplate.headerText) {
          headerVariables = extractVariables(selectedTemplate.headerText);
        }
        
        const allVariables = [...new Set([...bodyVariables, ...headerVariables])];
        
        // Initialize all variables with empty values
        const initialVars = {};
        allVariables.forEach(expression => {
          initialVars[expression] = {
            type: 'TEXT',
            value: ''
          };
        });
        
        // Populate with variables from node.data.variables (if available from backend)
        if (node.data.variables && typeof node.data.variables === 'object') {
          Object.keys(node.data.variables).forEach(expression => {
            if (initialVars[expression] !== undefined) {
              initialVars[expression] = {
                type: node.data.variables[expression].type || 'TEXT',
                value: node.data.variables[expression].value || ''
              };
            }
          });
        }
        
        // Backward compatibility: check for single variable fields
        if (!node.data.variables && node.data.variableExpression && node.data.variableType) {
          if (initialVars[node.data.variableExpression] !== undefined) {
            initialVars[node.data.variableExpression] = {
              type: node.data.variableType,
              value: node.data.value || ''
            };
          }
        }
        
        setTemplateVariables(initialVars);
      } else {
        // If template not found, use variables from node data if available
        if (node.data.variables && typeof node.data.variables === 'object') {
          setTemplateVariables(node.data.variables);
        } else {
          setTemplateVariables({});
        }
      }
      
      console.log('FormData set with templateId:', node.data.templateId);
      console.log('Template variables:', templateVariables);
    }
  }, [node, templates]); // Add templates to dependency array

  // Debug: Monitor formData changes
  useEffect(() => {
    console.log('FormData updated:', formData);
    console.log('FormData templateId:', formData.templateId, typeof formData.templateId);
  }, [formData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.label.trim()) {
      newErrors.label = 'Node name is required';
    }

    if (formData.type === 'template' && !formData.templateId) {
      newErrors.templateId = 'Please select a template';
    }

    // Remove the freeText validation since it's no longer available
    // if (formData.type === 'freeText' && !formData.content.trim()) {
    //   newErrors.content = 'Content is required for free text';
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      // Include all variables in the update
      onUpdate(node.id, {
        ...formData,
        variables: templateVariables
      });
      onClose();
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this node?')) {
      onDelete(node.id);
    }
  };

  const handleVariablesChange = (variables) => {
    setTemplateVariables(variables);
    // All variables are stored in templateVariables state
    // No need to store in formData anymore
  };

  const isStartNode = node?.id === 'start-node' || node?.data?.isStart;
  const isReadOnly = botStatus !== '10001'; // Only allow editing for draft bots

  return (
    <div className="w-80 bg-white shadow-lg border-l border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Node Configuration</h3>
          {isReadOnly && (
            <p className="text-sm text-gray-500 italic">
              {botStatus === '10002' ? 'Read-only: Published bot' : 
               botStatus === '10003' ? 'Read-only: Archived bot' : 'Read-only'}
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-md transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 space-y-6 overflow-y-auto">
        {/* Node Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Node Name *
          </label>
          <input
            type="text"
            value={formData.label}
            onChange={(e) => handleInputChange('label', e.target.value)}
            disabled={isReadOnly}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isReadOnly ? 'bg-gray-100 cursor-not-allowed' : 
              errors.label ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter node name"
          />
          {errors.label && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.label}
            </p>
          )}
        </div>

        {/* Message Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message Type *
          </label>
          <select
            value={formData.type}
            onChange={(e) => handleInputChange('type', e.target.value)}
            disabled={isReadOnly}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isReadOnly ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
            }`}
          >
            <option value="template">Approved Templates</option>
          </select>
        </div>

        {/* Template Selection */}
        {formData.type === 'template' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Template *
            </label>
            <select
              value={formData.templateId || ''}
              onChange={(e) => handleInputChange('templateId', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isReadOnly ? 'bg-gray-100 cursor-not-allowed' :
                errors.templateId ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={loading || isReadOnly}
            >
              <option value="">Choose a template...</option>
              {templates.map((template) => (
                <option key={template.templateId} value={template.templateId}>
                  {template.templateName} - {template.templateCategory}
                </option>
              ))}
            </select>
            {errors.templateId && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.templateId}
              </p>
            )}
            {loading && (
              <p className="mt-1 text-sm text-gray-500">Loading templates...</p>
            )}
          </div>
        )}

        {/* Selected Template Preview */}
        {formData.type === 'template' && formData.templateId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template Preview
            </label>
            <TemplateVariableHandler
              template={templates.find(t => t.templateId === formData.templateId)}
              initialVariables={templateVariables}
              onVariablesChange={handleVariablesChange}
              showPreview={true}
              disabled={isReadOnly}
            />
          </div>
        )}

      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 flex space-x-3">
        <button
          onClick={handleSave}
          disabled={isReadOnly}
          className={`flex-1 px-4 py-2 rounded-md flex items-center justify-center space-x-2 transition-colors ${
            isReadOnly 
              ? 'bg-gray-400 cursor-not-allowed text-gray-200' 
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
          title={isReadOnly ? 'Cannot save non-draft bot' : 'Save changes'}
        >
          <Save className="w-4 h-4" />
          <span>Save</span>
        </button>
        <button
          onClick={handleDelete}
          disabled={isReadOnly}
          className={`px-4 py-2 border rounded-md flex items-center space-x-2 transition-colors ${
            isReadOnly 
              ? 'border-gray-300 text-gray-400 cursor-not-allowed' 
              : 'border-red-300 text-red-600 hover:bg-red-50'
          }`}
          title={isReadOnly ? 'Cannot delete from non-draft bot' : 'Delete node'}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default NodeSidebar;